import React, { FC, useState } from 'react';
import styled from 'styled-components';
import i18n from 'i18n';

import Dialog from '../Dialog/index';
import Typography from '../Typography';
import Radio from '../Toggle/Radio';
import App from '../../../models/app';

const RadioRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0px;
  cursor: pointer;
  border-bottom: 1px solid var(--sys-color--outline-variant);
  &:last-child {
    border-bottom: none;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid var(--sys-color--outline);
  background-color: transparent;
  color: var(--sys-color--on-surface);
  border-radius: 12px;
  height: 98px;
  margin-top: 12px;
  padding: 12px;
  resize: none;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  font-family: inherit;
  font-size: 16px;
  &:focus {
    border-color: var(--sys-color--primary);
  }
`;

const ErrorText = styled(Typography)`
  color: var(--sys-color--error);
  margin-bottom: 8px;
`;

interface ReportModalProps {
  app: App;
  onClose: () => void;
}

type Mode = 'reporting' | 'reportDMCA' | 'reportText' | 'reported';

export const ReportModal: FC<ReportModalProps> = ({ app, onClose }) => {
  const [mode, setMode] = useState<Mode>('reporting');
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reportEmailLink = (reason: string, details: string) => {
    const encodedBody = encodeURIComponent(
      `I am reporting this app because: ${reason} \r\rDetails: ${details} \r\rApp Name: ${app.name}`
    );
    return `mailto:appgallerysupport@fitbit.com?subject=App Gallery Private App Report&body=${encodedBody}`;
  };

  const handleReport = () => {
    if (!selectedReason) {
      setError(i18n('component.ReportModal.Error')());
      return;
    }

    if (selectedReason === 'copyright' || selectedReason === 'trademark') {
      setMode('reportDMCA');
    } else {
      setMode('reportText');
    }
  };

  const handleSubmitTextReport = () => {
    const emailLink = reportEmailLink(selectedReason || '', description);
    window.location.href = emailLink;
    setMode('reported');
  };

  // Render different steps
  switch (mode) {
    case 'reporting': {
      const reportTypes = i18n('component.ReportModal.ReportTypes');
      const items = Object.keys(reportTypes).map((key) => ({
        value: key,
        title: reportTypes[key](),
      }));

      return (
        <Dialog
          show={true}
          id="ReportModal"
          title={i18n('component.ReportModal.Title')()}
          onDismiss={onClose}
          actions={[
            {
              text: i18n('component.ReportModal.Cancel')(),
              onClick: onClose,
              variant: 'text',
              id: 'report-button-cancel',
            },
            {
              text: i18n('component.ReportModal.Report')(),
              onClick: handleReport,
              variant: 'filled',
              id: 'report-button-submit',
            },
          ]}
        >
          {error && (
            <ErrorText variant="bodyMedium">
              {error}
            </ErrorText>
          )}
          <div>
            {items.map((item) => (
              <RadioRow key={item.value} onClick={() => {
                setSelectedReason(item.value);
                setError(null);
              }}>
                <Typography variant="bodyLarge" color="var(--sys-color--on-surface)">
                  {item.title}
                </Typography>
                <Radio checked={selectedReason === item.value} />
              </RadioRow>
            ))}
          </div>
        </Dialog>
      );
    }

    case 'reportDMCA':
      return (
        <Dialog
          show={true}
          id="ReportedDMCAModal"
          title={i18n('component.ReportedModal.DMCATitle')()}
          onDismiss={onClose}
          actions={[
            {
              text: i18n('component.ReportedModal.Ok')(),
              onClick: onClose,
              variant: 'filled',
              id: 'report-dmca-ok',
            },
          ]}
        >
          <Typography variant="bodyMedium" color="var(--sys-color--on-surface-variant)">
            {i18n('component.ReportedModal.DMCAText')()}
            &nbsp;
            <a
              href="https://dev.fitbit.com/legal/reporting-apps/"
              target="_blank"
              rel="noopener noreferrer"
            >
              fitbit.com/copyright-infringement
            </a>
          </Typography>
        </Dialog>
      );

    case 'reportText':
      return (
        <Dialog
          show={true}
          id="ReportTextModal"
          title={i18n('component.ReportTextModal.Title')()}
          onDismiss={onClose}
          actions={[
            {
              text: i18n('component.ReportTextModal.Cancel')(),
              onClick: onClose,
              variant: 'text',
              id: 'report-text-cancel',
            },
            {
              text: i18n('component.ReportTextModal.Submit')(),
              onClick: handleSubmitTextReport,
              variant: 'filled',
              id: 'report-text-submit',
            },
          ]}
        >
          <Typography variant="bodyMedium" color="var(--sys-color--on-surface-variant)">
            {i18n('component.ReportTextModal.Placeholder')()}
          </Typography>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={i18n('component.ReportTextModal.Placeholder')()}
            autoFocus
          />
        </Dialog>
      );

    case 'reported':
      return (
        <Dialog
          show={true}
          id="ReportedModal"
          title={i18n('component.ReportedModal.Title')()}
          onDismiss={onClose}
          actions={[
            {
              text: i18n('component.ReportedModal.Ok')(),
              onClick: onClose,
              variant: 'filled',
              id: 'report-success-ok',
            },
          ]}
        >
          <Typography variant="bodyMedium" color="var(--sys-color--on-surface-variant)">
            {i18n('component.ReportedModal.OtherText')()}
          </Typography>
        </Dialog>
      );

    default:
      return null;
  }
};

export default ReportModal;
